import type { HomeRoomsAndBeds, PriceRange } from "@b2bportal/core-types";
import {
  type Amenity,
  type HotelStarRating,
  type Lodging,
  type LodgingHomesAmenityKind,
  MealPlanKind,
  StayType,
} from "@b2bportal/lodging-api";
import { intersection, isEqual } from "lodash-es";

export const performStarRatingFilter = (
  lodging: Lodging,
  starRatingFilter: HotelStarRating[]
) => {
  return (
    starRatingFilter.length === 0 ||
    starRatingFilter.includes(lodging.lodging.starRating)
  );
};

export const performPriceFilter = (
  lodging: Lodging,
  hasSetPriceFilter: boolean,
  priceFilter: PriceRange
) => {
  return (
    !hasSetPriceFilter ||
    (lodging.price != null &&
      priceFilter.min <=
        lodging.price.nightlyDiscountAware.priceWithUnmanagedDiscounts.fiat
          .value &&
      priceFilter.max >=
        lodging.price.nightlyDiscountAware.priceWithUnmanagedDiscounts.fiat
          .value)
  );
};

export const performCancellationFilter = (
  lodging: Lodging,
  cancellationFilter: boolean
) => {
  return !cancellationFilter || (cancellationFilter && lodging.isFreeCancel);
};

export const performAmenitiesFilter = (
  lodging: Lodging,
  amenitiesFilter: Amenity[],
  stayTypeFilter: StayType[]
) => {
  if (!getIsHomesOnlyStayTypeSelected(stayTypeFilter)) {
    return amenitiesFilter.every((amenity) =>
      lodging.lodging.amenities.some(
        (hotelAmenity) => hotelAmenity.amenity === amenity
      )
    );
  }
  return true;
};

export const performPayLaterFilter = (
  lodging: Lodging,
  payLaterFilter: boolean
) => {
  return !payLaterFilter || lodging.highlights?.hasPayLater === true;
};

export const performMealPlanFilter = (
  lodging: Lodging,
  mealPlanFilter: MealPlanKind[]
) => {
  return (
    mealPlanFilter == null ||
    mealPlanFilter?.length === 0 ||
    mealPlanFilter?.includes(
      lodging.price?.mealPlan?.kind ?? MealPlanKind.RoomOnly
    )
  );
};

export const performHotelNameFilter = (
  lodging: Lodging,
  hotelNameFilter: string | undefined
) => {
  return (
    hotelNameFilter == null ||
    hotelNameFilter === "" ||
    lodging.lodging.name.toLowerCase().includes(hotelNameFilter.toLowerCase())
  );
};

export const getIsHomesOnlyStayTypeSelected = (
  stayTypes: StayType[]
): boolean => {
  const stayTypesSet = new Set(stayTypes);
  return stayTypesSet.size === 1 && stayTypesSet.has(StayType.Homes);
};

export const performStayTypeFilter = (
  lodging: Lodging,
  selectedStayTypes: StayType[]
): boolean => {
  if (selectedStayTypes.length === 0) {
    return true;
  }

  return selectedStayTypes.includes(lodging.stayType ?? StayType.Hotels);
};

export const performHomesRoomsAndBedsFilter = (
  lodging: Lodging,
  roomsAndBedsFilter: HomeRoomsAndBeds | undefined,
  stayTypes: StayType[]
): boolean => {
  const shouldApplyFilter =
    roomsAndBedsFilter != null &&
    lodging.stayType === StayType.Homes &&
    getIsHomesOnlyStayTypeSelected(stayTypes);

  if (shouldApplyFilter) {
    const roomsAndBeds =
      lodging.homesListingDetails?.listingContent?.listingLayout;

    if (roomsAndBeds) {
      if (
        roomsAndBedsFilter.numberOfBedrooms != null &&
        roomsAndBedsFilter.numberOfBedrooms > 0
      ) {
        if (
          (roomsAndBeds.numberOfBedrooms || 0) <
          roomsAndBedsFilter.numberOfBedrooms
        ) {
          return false;
        }
      }
      if (
        roomsAndBedsFilter.numberOfBathrooms != null &&
        roomsAndBedsFilter.numberOfBathrooms > 0
      ) {
        if (
          (roomsAndBeds.numberOfBathrooms || 0) <
          roomsAndBedsFilter.numberOfBathrooms
        ) {
          return false;
        }
      }
      if (
        roomsAndBedsFilter.numberOfBeds != null &&
        roomsAndBedsFilter.numberOfBeds > 0
      ) {
        if (
          (roomsAndBeds.numberOfBeds || 0) < roomsAndBedsFilter.numberOfBeds
        ) {
          return false;
        }
      }
    }
  }
  return true;
};

// const homeAmenityToFilterAmenityMap: { [key in LodgingHomesAmenityKind]?: LodgingHomesAmenityKind } =
//   {
//     [LodgingHomesAmenityKind.Kitchenette]: LodgingHomesAmenityKind.Kitchen,
//   };

// export const homeAmenityToTopLodgingAmenityMap: {
//   [key in LodgingHomesAmenityKind]?: TopAmenityEnum;
// } = {
//   [LodgingHomesAmenityKind.Pool]: TopAmenityEnum.Pool,
//   [LodgingHomesAmenityKind.Parking]: TopAmenityEnum.ParkingIncluded,
//   [LodgingHomesAmenityKind.Wifi]: TopAmenityEnum.FreeWifi,
//   [LodgingHomesAmenityKind.PetFriendly]: TopAmenityEnum.PetFriendly,
//   [LodgingHomesAmenityKind.DedicatedWorkspace]: TopAmenityEnum.BusinessServices,
//   [LodgingHomesAmenityKind.Dryer]: TopAmenityEnum.Laundry,
// };

export const performHomeAmenitiesFilter = (
  lodging: Lodging,
  amenitiesFilter: LodgingHomesAmenityKind[],
  stayTypes: StayType[]
): boolean => {
  const shouldApplyFilter =
    amenitiesFilter.length > 0 &&
    lodging.stayType === StayType.Homes &&
    getIsHomesOnlyStayTypeSelected(stayTypes);

  if (shouldApplyFilter) {
    const homeAmenityKinds = (
      lodging.homesListingDetails?.listingContent?.amenities ?? []
    ).map((amenity) => amenity.kind);

    // const resolvedHomeAmenityKinds = homeAmenityKinds.map(
    //   (kind) => homeAmenityToFilterAmenityMap[kind] || kind
    // );

    // const lodgingAmenitiesList = (lodging.lodging.amenities ?? []).map(
    //   (status) => status.amenity
    // );

    // const homeAmenityKindsDerivedFromLodgingAmenities =
    //   mapAmenitiesToTopAmenities(lodgingAmenitiesList)
    //     .map(
    //       (topAmenity) => topLodgingAmenityToHomeAmenityMap[topAmenity.value]
    //     )
    //     .filter(
    //       (homeAmenity): homeAmenity is HomeAmenityKind =>
    //         homeAmenity !== undefined
    //     );

    // Combine Home amenities from both sources to mainly handle the case where pet friendly
    // is not explicitly set in the Home amenities but is set in the lodging amenities.
    // const combinedHomeAmenities = [
    //   ...resolvedHomeAmenityKinds,
    //   ...homeAmenityKindsDerivedFromLodgingAmenities,
    // ];

    return isEqual(
      intersection(amenitiesFilter, homeAmenityKinds),
      amenitiesFilter
    );
  }
  return true;
};

export const performUserRatingFilter = (
  lodging: Lodging,
  userRatingFilter: number
) => {
  return !userRatingFilter || lodging.lodging.score >= userRatingFilter;
};

export const performAvailabilityFilter = (
  lodging: Lodging,
  hideUnavailableLodgings: boolean
) => {
  return !hideUnavailableLodgings || lodging.available !== false;
};
